import { Injectable, inject } from '@angular/core';
import {
  Address,
  Cart,
  UserEnum,
  DeliveryMode,
  Order,
  ProductReference,
  PaymentDetails,
  EntryGroup,
  BOProduct,
  OrderEntryGroup,
  EntryGroupUpdate,
  GroupOrderModification,
} from '@boels/models';
import { Store, select } from '@ngrx/store';
import { Observable, filter, of, switchMap } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import {
  addToCart,
  placeOrderChallenge,
  removeFromCart,
  setPaymentInfo,
  setCheckoutAccount,
  setCheckoutStepNumber,
  setDeliveryAddress,
  setDeliveryMode,
  updateCart,
  updateCartDates,
  updateCartMode,
} from '../actions/cart.action';
import { CartGatewayService } from '../gateway/cart.gateway';
import { CartState } from '../reducers/cart.reducer';
import * as fromSelector from '../selectors/cart.selectors';

@Injectable({
  providedIn: 'root',
})
export class CartFacade {
  protected store = inject<Store<CartState>>(Store);
  private cartGateway = inject(CartGatewayService);

  getCartLoading(): Observable<boolean> {
    return this.store.pipe(select(fromSelector.selectCartLoading));
  }

  getActiveCart(canReturnEmpty = false): Observable<Cart> {
    const cart$ = this.store.pipe(select(fromSelector.selectActiveCart));

    if (!canReturnEmpty) {
      return cart$.pipe(filter(Boolean));
    }

    return cart$;
  }

  getActiveCartId(): Observable<string> {
    return this.store.pipe(select(fromSelector.selectActiveCartId));
  }

  getCheckoutAccount(): Observable<UserEnum> {
    return this.store.pipe(select(fromSelector.selectCheckoutAccount));
  }

  setCheckoutAccount(checkoutAccount: UserEnum): void {
    this.store.dispatch(
      setCheckoutAccount({ checkoutAccount: checkoutAccount }),
    );
  }

  setActiveStepNumber(checkoutStepNumber: number) {
    this.store.dispatch(setCheckoutStepNumber({ checkoutStepNumber }));
  }

  getActiveStepNumber(): Observable<number> {
    return this.store.pipe(select(fromSelector.selectActiveCheckoutStepNumber));
  }

  addEntry(entryGroup: EntryGroup): Observable<CartState> {
    this.store.dispatch(addToCart(entryGroup));
    return this.store.pipe(
      select(fromSelector.selectCartState),
      filter((state) => state.processCount === 0 && !state.loading),
    );
  }

  updateEntry(entryGroup: EntryGroupUpdate) {
    this.store.dispatch(updateCart(entryGroup));
    return this.store.pipe(
      select(fromSelector.selectCartState),
      filter((state) => state.processCount === 0 && !state.loading),
    );
  }

  removeEntry(entryNumber: number) {
    this.store.dispatch(removeFromCart({ entryNumber }));
  }

  getActiveCartReferences(
    referenceTypes: string,
  ): Observable<ProductReference[]> {
    return this.getActiveCart().pipe(
      filter(Boolean),
      switchMap((activeCart: Cart) => {
        if (activeCart.entries && activeCart.entries.length > 0) {
          return this.getCartReferences(referenceTypes).pipe(
            map((refs: any) => refs.references),
          );
        } else return of([]);
      }),
    );
  }

  getCartReferences(referenceTypes: string) {
    return this.cartGateway.getCartReference(referenceTypes);
  }

  getReservationCalendar(startDate: string, endDate: string): Observable<any> {
    return this.cartGateway.getReservationCalendar(startDate, endDate);
  }

  convertCartToAnonymous(): Observable<Cart> {
    return this.cartGateway.assignCartToAnonymous();
  }

  updateCartMode(
    pointOfServiceName?: string,
    useRentalWall?: boolean,
  ): Observable<GroupOrderModification> {
    this.store.dispatch(
      updateCartMode({
        pointOfServiceName: pointOfServiceName as string,
        useRentalWall: useRentalWall as boolean,
      }),
    );
    return this.store.pipe(select(fromSelector.selectModifiedCart));
  }

  updateCartDates(startDate: string, endDate: string): void {
    this.store.dispatch(updateCartDates({ startDate, endDate }));
  }

  placeOrderWithRemarksAndNewsletter(
    termsChecked: boolean,
    remarks?: string,
    newsletter?: boolean,
  ): void {
    this.store.dispatch(
      placeOrderChallenge({
        termsChecked,
        remarks,
        newsletter,
      }),
    );
  }

  getOrderDetails(): Observable<Order> {
    return this.store
      .pipe(select(fromSelector.selectOrder))
      .pipe(filter(Boolean));
  }

  getProductOrderEntry(
    product: BOProduct | undefined,
  ): Observable<OrderEntryGroup | undefined> {
    if (!product) return of(undefined);
    return this.getActiveCart().pipe(
      filter(Boolean),
      shareReplay(1),
      map((activeCart: Cart) => {
        if (activeCart.entries && activeCart.entries.length > 0) {
          return activeCart.entries.find(
            (entry) => entry.product?.code === product.code,
          );
        } else return undefined;
      }),
    );
  }

  setDeliveryAddress(deliveryAddress: Address | undefined): void {
    this.store.dispatch(setDeliveryAddress({ deliveryAddress }));
  }

  setDeliveryMode(deliveryMode: DeliveryMode | undefined): void {
    this.store.dispatch(setDeliveryMode({ deliveryMode }));
  }

  setPaymentDetails(paymentDetails: PaymentDetails | undefined): void {
    this.store.dispatch(setPaymentInfo({ paymentDetails }));
  }
}
